import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_PATHS } from '../config/api-paths';
import { SmePaymentsDraftsApi } from '../tokens/sme-payments-drafts.token';
import { PaymentDraftRequest, PaymentOrderDto } from '../models/sme-payment.model';

/**
 * REST-only implementation of `SmePaymentsDraftsApi` (EM-357, C4).
 *
 * Calls (party-scoped server-side from the EM-306 session — no client partyId):
 *   POST   /services/embedded/api/payments/drafts
 *   PATCH  /services/embedded/api/payments/drafts/{id}
 *   GET    /services/embedded/api/payments/drafts
 *   DELETE /services/embedded/api/payments/drafts/{id}
 *   POST   /services/embedded/api/payments/{id}/cancel
 *
 * Rides the existing Keycloak bearer (attached by `authInterceptor`). Rule 07 / Rule 10 — no Mock
 * fallback; Playwright specs intercept via `page.route()`. Errors propagate as `HttpErrorResponse`;
 * consumers surface the backend message via `extractBackendErrorMessage` (Rule 09) — in particular the
 * cancel 409 (`paymentNotCancellable`) and the draft-edit/delete 409 (`draftNotEditable` /
 * `draftNotDeletable`).
 */
@Injectable()
export class SmePaymentsDraftsRestService implements SmePaymentsDraftsApi {
  private readonly http = inject(HttpClient);
  private readonly base = API_PATHS.PAYMENTS;

  saveDraft(request: PaymentDraftRequest): Observable<PaymentOrderDto> {
    return this.http.post<PaymentOrderDto>(`${this.base}/drafts`, this.normalise(request));
  }

  updateDraft(draftId: number, request: PaymentDraftRequest): Observable<PaymentOrderDto> {
    return this.http.patch<PaymentOrderDto>(`${this.base}/drafts/${draftId}`, this.normalise(request));
  }

  listDrafts(): Observable<PaymentOrderDto[]> {
    return this.http.get<PaymentOrderDto[]>(`${this.base}/drafts`);
  }

  deleteDraft(draftId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/drafts/${draftId}`);
  }

  cancelPayment(paymentId: number): Observable<PaymentOrderDto> {
    return this.http.post<PaymentOrderDto>(`${this.base}/${paymentId}/cancel`, {});
  }

  /** Sends `category` UPPERCASE to match the backend enum when present (mirrors the submit path). */
  private normalise(request: PaymentDraftRequest): PaymentDraftRequest {
    return request.category ? { ...request, category: request.category.toUpperCase() } : request;
  }
}
